import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DollarSign, TrendingUp, Edit, Trash2, Plus, Save, X, History, ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Image Preview Component with loading and error states
const ImagePreview = ({ url, onValidChange }) => {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  
  useEffect(() => {
    if (!url || url.trim() === '') {
      setStatus('idle');
      onValidChange?.(false);
      return;
    }
    
    setStatus('loading');
    const img = new Image();
    img.onload = () => {
      setStatus('success');
      onValidChange?.(true);
    };
    img.onerror = () => {
      setStatus('error');
      onValidChange?.(false);
    };
    img.src = url;
  }, [url]);

  if (!url || url.trim() === '') {
    return (
      <div className="mt-2 flex items-center gap-2 text-gray-400">
        <ImageIcon className="h-4 w-4" />
        <span className="text-sm">No image URL provided - emoji will be used</span>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {status === 'loading' && (
        <div className="flex items-center gap-2 text-blue-600">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="text-sm">Loading preview...</span>
        </div>
      )}
      {status === 'success' && (
        <div className="flex items-center gap-3">
          <img 
            src={url} 
            alt="Vehicle preview" 
            className="w-24 h-16 object-cover rounded-lg border-2 border-green-200 shadow-sm"
          />
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Image loaded</span>
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">Failed to load image - check URL</span>
        </div>
      )}
    </div>
  );
};

const AdminPricing = () => {
  const [globalSettings, setGlobalSettings] = useState({ basePrice: 3.50, perMile: 1.80 });
  const [rideTypes, setRideTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGlobal, setEditingGlobal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [pricingHistory, setPricingHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Form states
  const [globalForm, setGlobalForm] = useState({ basePrice: '', perMile: '' });
  const [typeForm, setTypeForm] = useState({});
  const [newTypeForm, setNewTypeForm] = useState({
    typeId: '',
    name: '',
    description: '',
    capacity: 4,
    icon: '🚗',
    image: '',
    priceMultiplier: 1.0,
    basePrice: '',
    perMile: '',
    eta: '2-5 min'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch global settings
      const globalResponse = await api.get('/admin/pricing/global-settings');
      if (globalResponse.data.success) {
        setGlobalSettings(globalResponse.data.settings);
        setGlobalForm(globalResponse.data.settings);
      }

      // Fetch ride types
      const typesResponse = await api.get('/admin/pricing/ride-types');
      if (typesResponse.data.success) {
        setRideTypes(typesResponse.data.rideTypes);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching pricing data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pricing data',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/admin/pricing/history');
      if (response.data.success) {
        setPricingHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleUpdateGlobal = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.put('/admin/pricing/global-settings', {
        basePrice: parseFloat(globalForm.basePrice),
        perMile: parseFloat(globalForm.perMile)
      });

      if (response.data.success) {
        setGlobalSettings(response.data.settings);
        setEditingGlobal(false);
        toast({
          title: 'Success',
          description: response.data.message,
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update global settings',
        variant: 'destructive',
      });
    }
  };

  const handleEditType = (type) => {
    setEditingType(type.typeId);
    setTypeForm({
      name: type.name,
      description: type.description,
      capacity: type.capacity,
      icon: type.icon,
      image: type.image || '',
      priceMultiplier: type.priceMultiplier,
      basePrice: type.basePrice || '',
      perMile: type.perMile || '',
      eta: type.eta
    });
  };

  const handleUpdateType = async (typeId) => {
    try {
      const api = (await import('../../utils/api')).default;
      const updateData = {
        name: typeForm.name,
        description: typeForm.description,
        capacity: parseInt(typeForm.capacity),
        icon: typeForm.icon,
        priceMultiplier: parseFloat(typeForm.priceMultiplier),
        eta: typeForm.eta
      };

      if (typeForm.basePrice) updateData.basePrice = parseFloat(typeForm.basePrice);
      if (typeForm.perMile) updateData.perMile = parseFloat(typeForm.perMile);
      if (typeForm.image !== undefined) updateData.image = typeForm.image || null;

      const response = await api.put(`/admin/pricing/ride-types/${typeId}`, updateData);

      if (response.data.success) {
        setEditingType(null);
        toast({
          title: 'Success',
          description: response.data.message,
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update ride type',
        variant: 'destructive',
      });
    }
  };

  const handleCreateType = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.post('/admin/pricing/ride-types', {
        ...newTypeForm,
        capacity: parseInt(newTypeForm.capacity),
        priceMultiplier: parseFloat(newTypeForm.priceMultiplier),
        basePrice: parseFloat(newTypeForm.basePrice || globalSettings.basePrice),
        perMile: parseFloat(newTypeForm.perMile || globalSettings.perMile),
        image: newTypeForm.image || null
      });

      if (response.data.success) {
        setShowAddForm(false);
        setNewTypeForm({
          typeId: '',
          name: '',
          description: '',
          capacity: 4,
          icon: '🚗',
          image: '',
          priceMultiplier: 1.0,
          basePrice: '',
          perMile: '',
          eta: '2-5 min'
        });
        toast({
          title: 'Success',
          description: response.data.message,
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create ride type',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this ride type?')) return;

    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.delete(`/admin/pricing/ride-types/${typeId}`);

      if (response.data.success) {
        toast({
          title: 'Success',
          description: response.data.message,
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete ride type',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pricing Management</h1>
          <p className="text-gray-600 mt-1">Manage ride rates and pricing settings</p>
        </div>
        <Button
          onClick={() => {
            setShowHistory(!showHistory);
            if (!showHistory) fetchHistory();
          }}
          variant="outline"
        >
          <History className="mr-2 h-4 w-4" />
          {showHistory ? 'Hide History' : 'View History'}
        </Button>
      </div>

      {/* Global Pricing Settings */}
      <Card className="p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Global Pricing Defaults
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Default base price and per-kilometer rate for all ride types
            </p>
          </div>
          {!editingGlobal && (
            <Button onClick={() => setEditingGlobal(true)} variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>

        {editingGlobal ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="basePrice">Base Price (K)</Label>
                <Input
                  id="basePrice"
                  type="number"
                  step="0.01"
                  value={globalForm.basePrice}
                  onChange={(e) => setGlobalForm({ ...globalForm, basePrice: e.target.value })}
                  placeholder="3.50"
                />
              </div>
              <div>
                <Label htmlFor="perMile">Per Kilometer (K)</Label>
                <Input
                  id="perMile"
                  type="number"
                  step="0.01"
                  value={globalForm.perMile}
                  onChange={(e) => setGlobalForm({ ...globalForm, perMile: e.target.value })}
                  placeholder="1.80"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdateGlobal}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingGlobal(false);
                  setGlobalForm(globalSettings);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700 font-medium">Base Price</p>
              <p className="text-3xl font-bold text-green-900">K{globalSettings.basePrice.toFixed(2)}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700 font-medium">Per Kilometer</p>
              <p className="text-3xl font-bold text-blue-900">K{globalSettings.perMile.toFixed(2)}</p>
            </div>
          </div>
        )}
      </Card>

      {/* Pricing History */}
      {showHistory && (
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Pricing Change History</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Action</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pricingHistory.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm">{new Date(item.changedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm capitalize">{item.type.replace('_', ' ')}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.action === 'create' ? 'bg-green-100 text-green-800' :
                        item.action === 'update' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.action}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {JSON.stringify(item.changes || item.data, null, 2).substring(0, 100)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Ride Types */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">Ride Types</h2>
        <Button onClick={() => setShowAddForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Ride Type
        </Button>
      </div>

      {/* Add New Ride Type Form */}
      {showAddForm && (
        <Card className="p-6 mb-6 border-2 border-pink-200">
          <h3 className="text-lg font-bold mb-4">Add New Ride Type</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type ID (unique)</Label>
              <Input
                value={newTypeForm.typeId}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, typeId: e.target.value })}
                placeholder="premium"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={newTypeForm.name}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, name: e.target.value })}
                placeholder="Premium"
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input
                value={newTypeForm.description}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, description: e.target.value })}
                placeholder="Premium rides with extra comfort"
              />
            </div>
            <div>
              <Label>Capacity</Label>
              <Input
                type="number"
                value={newTypeForm.capacity}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, capacity: e.target.value })}
              />
            </div>
            <div>
              <Label>Icon (emoji)</Label>
              <Input
                value={newTypeForm.icon}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, icon: e.target.value })}
                placeholder="🚗"
              />
            </div>
            <div className="col-span-2">
              <Label>Image URL (car picture)</Label>
              <Input
                value={newTypeForm.image}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, image: e.target.value })}
                placeholder="https://images.unsplash.com/photo-..."
                data-testid="new-ride-type-image-url"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use Unsplash URLs. Recommended size: 200x120. Leave empty to use emoji icon.
              </p>
              <ImagePreview url={newTypeForm.image} />
            </div>
            <div>
              <Label>Price Multiplier</Label>
              <Input
                type="number"
                step="0.1"
                value={newTypeForm.priceMultiplier}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, priceMultiplier: e.target.value })}
                placeholder="1.0"
              />
            </div>
            <div>
              <Label>ETA</Label>
              <Input
                value={newTypeForm.eta}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, eta: e.target.value })}
                placeholder="2-5 min"
              />
            </div>
            <div>
              <Label>Base Price (optional, uses global if empty)</Label>
              <Input
                type="number"
                step="0.01"
                value={newTypeForm.basePrice}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, basePrice: e.target.value })}
                placeholder={`${globalSettings.basePrice} (global)`}
              />
            </div>
            <div>
              <Label>Per Kilometer (optional, uses global if empty)</Label>
              <Input
                type="number"
                step="0.01"
                value={newTypeForm.perMile}
                onChange={(e) => setNewTypeForm({ ...newTypeForm, perMile: e.target.value })}
                placeholder={`${globalSettings.perMile} (global)`}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleCreateType}>
              <Save className="h-4 w-4 mr-2" />
              Create Ride Type
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Ride Types List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rideTypes.map((type) => (
          <Card key={type.typeId} className="p-6">
            {editingType === type.typeId ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={typeForm.name}
                      onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Icon</Label>
                    <Input
                      value={typeForm.icon}
                      onChange={(e) => setTypeForm({ ...typeForm, icon: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Image URL (car picture)</Label>
                    <Input
                      value={typeForm.image || ''}
                      onChange={(e) => setTypeForm({ ...typeForm, image: e.target.value })}
                      placeholder="https://images.unsplash.com/photo-..."
                      data-testid="edit-ride-type-image-url"
                    />
                    <ImagePreview url={typeForm.image} />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Input
                      value={typeForm.description}
                      onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={typeForm.capacity}
                      onChange={(e) => setTypeForm({ ...typeForm, capacity: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Multiplier</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={typeForm.priceMultiplier}
                      onChange={(e) => setTypeForm({ ...typeForm, priceMultiplier: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Base Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={typeForm.basePrice}
                      onChange={(e) => setTypeForm({ ...typeForm, basePrice: e.target.value })}
                      placeholder={`${globalSettings.basePrice} (global)`}
                    />
                  </div>
                  <div>
                    <Label>Per Kilometer</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={typeForm.perMile}
                      onChange={(e) => setTypeForm({ ...typeForm, perMile: e.target.value })}
                      placeholder={`${globalSettings.perMile} (global)`}
                    />
                  </div>
                  <div>
                    <Label>ETA</Label>
                    <Input
                      value={typeForm.eta}
                      onChange={(e) => setTypeForm({ ...typeForm, eta: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleUpdateType(type.typeId)}>
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingType(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    {type.image ? (
                      <img 
                        src={type.image} 
                        alt={type.name}
                        className="w-16 h-10 object-cover rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span className={`text-4xl ${type.image ? 'hidden' : ''}`}>{type.icon}</span>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{type.name}</h3>
                      <p className="text-sm text-gray-600">{type.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEditType(type)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeleteType(type.typeId)}>
                      <Trash2 className="h-3 w-3 text-red-600" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Capacity</p>
                    <p className="font-semibold">{type.capacity} people</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Multiplier</p>
                    <p className="font-semibold">{type.priceMultiplier}x</p>
                  </div>
                  <div>
                    <p className="text-gray-600">ETA</p>
                    <p className="font-semibold">{type.eta}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Base Price</p>
                      <p className="font-semibold">
                        K{type.basePrice?.toFixed(2) || `${globalSettings.basePrice.toFixed(2)} (global)`}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Per KM</p>
                      <p className="font-semibold">
                        K{type.perMile?.toFixed(2) || `${globalSettings.perMile.toFixed(2)} (global)`}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminPricing;
